import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import messages from './messages';
import styles from './CloverForm.module.scss';
import CloverInput from '../../CloverInput/CloverInput';
import {
  CLOVER_STYLES,
  CLOVER_CVV_STYLES,
  CLOVER_KEY,
  REQUIRED_ERROR,
  INVALID_ERROR,
  CARD_NUMBER_TYPE,
  CARD_POSTAL_CODE_TYPE,
  CARD_EXPIRY_TYPE,
  CARD_SECURITY_CODE_TYPE,
  CLOVER_SDK_URL,
} from './constants';
import { useCloverContext } from '../../../contexts/CloverContext';
import { CartInStore } from '../../../slices/cart/types';
import { CloverElement, CloverObj } from './types';
import { CloverFormErrorsKeys } from '../../../contexts/CloverContext/types';

const CloverForm = ({
  handleIsPaymentButtonDisabled,
}: {
  handleIsPaymentButtonDisabled: (isDisabled: boolean) => void;
}) => {
  const { formatMessage } = useIntl();


  const { cloverFormErrors, setCloverObject, cloverObject } =
    useCloverContext();
  const { billingAddress } = useSelector((state: { cart: CartInStore }) => state.cart);

  const [cardNumberError, setCardNumberError] = useState('');
  const [cardExpiryDateError, setCardExpiryDateError] = useState('');
  const [cardSecurityCodeError, setCardSecurityCodeError] = useState('');
  const [cardPostalCodeError, setCardPostalCodeError] = useState('');
  const [isCloverLoading, setIsCloverLoading] = useState(true);

  const formFieldErrorHandler = useCallback(
    (
      error: string,
      setErrorState: Dispatch<SetStateAction<string>>,
      requiredMessage: string,
      invalidMessage: string,
    ) => {
      setErrorState('');
      if (error?.includes(REQUIRED_ERROR)) {
        setErrorState(requiredMessage);
      }
      if (error?.includes(INVALID_ERROR)) {
        setErrorState(invalidMessage);
      }
    },
    [],
  );

  const addElementEventListener = useCallback(
    (
      element: CloverElement,
      setErrorState: Dispatch<SetStateAction<string>>,
      requiredMessage: string,
      invalidMessage: string,
    ) => {
      element.addEventListener('change', (event) => {
        handleIsPaymentButtonDisabled(false);
        const field = Object.entries(event).find(
          (e) => e[0] === element.elementType,
        );
        if (field && (field[1] as { error: string }).error) {
          formFieldErrorHandler(
            (field[1] as { error: string }).error,
            setErrorState,
            requiredMessage,
            invalidMessage,
          );
        }
      });
      element.addEventListener('blur', (event) => {
        setErrorState('');
        const field = Object.entries(event).find(
          (e) => e[0] === element.elementType,
        );
        if (field && (field[1] as { error: string }).error) {
          formFieldErrorHandler(
            (field[1] as { error: string }).error,
            setErrorState,
            requiredMessage,
            invalidMessage,
          );
        }
      });
    },
    [formFieldErrorHandler, handleIsPaymentButtonDisabled],
  );

  const renderCloverForm = useCallback(() => {
    if (!window.Clover) {
      // eslint-disable-next-line no-console
      console.warn('Clover is not available yet');
      return;
    }
    const { Clover } = window;
    
    const clover: CloverObj = new Clover(CLOVER_KEY, {
      merchantId: process.env.CLOVER_MERCHANT_ID,
    });
    setCloverObject(clover);

    if (clover.elements) {
      const elements = clover.elements();
      const cardNumber: CloverElement = elements.create('CARD_NUMBER', CLOVER_STYLES);
      const cardExpiryDate: CloverElement = elements.create('CARD_DATE', CLOVER_STYLES);
      const cardSecurityCode: CloverElement = elements.create('CARD_CVV', CLOVER_CVV_STYLES);
      const cardPostalCode: CloverElement = elements.create('CARD_POSTAL_CODE', CLOVER_STYLES);

      cardNumber.mount('#card-number');
      const invalidNumberMessage = formatMessage(messages.invalidCard);
      const requiredNumberMessage = formatMessage(messages.invalidCard);
      addElementEventListener(
        cardNumber,
        setCardNumberError,
        requiredNumberMessage,
        invalidNumberMessage,
      );

      cardExpiryDate.mount('#card-date');
      const invalidExpiryDateMessage = formatMessage(messages.invalidExp);
      const requiredExpiryDateMessage = formatMessage(messages.invalidExp);
      addElementEventListener(
        cardExpiryDate,
        setCardExpiryDateError,
        requiredExpiryDateMessage,
        invalidExpiryDateMessage,
      );

      cardSecurityCode.mount('#card-cvv');
      const invalidSecurityCodeMessage = formatMessage(
        messages.invalidSecurityCode,
      );
      const requiredSecurityCodeMessage = formatMessage(
        messages.emptySecurityCode,
      );
      addElementEventListener(
        cardSecurityCode,
        setCardSecurityCodeError,
        requiredSecurityCodeMessage,
        invalidSecurityCodeMessage,
      );
      cardPostalCode.mount('#card-postal-code');

      const requiredPostalCodeMessage = formatMessage(messages.emptyPostalCode);
      const invalidPostalCodeMessage = formatMessage(messages.invalidPostalCode);
      addElementEventListener(
        cardPostalCode,
        setCardPostalCodeError,
        requiredPostalCodeMessage,
        invalidPostalCodeMessage,
      );
    }

  }, [
    addElementEventListener,
    formatMessage,
    setCloverObject,
  ]);

  useEffect(() => {
    
  }, [billingAddress?.billingAddressDraft?.postalCode]);

  useEffect(() => {
    const cloverScript = document.getElementById('clover-script');
    if (!window.Clover && !cloverScript) {
      const s = document.createElement('script');
      s.id = 'clover-script';
      s.type = 'text/javascript';
      s.async = true;
      s.src = CLOVER_SDK_URL;
      s.onload = () => {
        if (window.Clover) {
          renderCloverForm();
        }
      };
      document.getElementsByTagName('body')[0].appendChild(s);
    } else if (window.Clover) {
        renderCloverForm();
      }
  }, [renderCloverForm]);

  useEffect(() => {
    const cloverFooter = document?.getElementsByClassName('clover-footer')[0];
    if (cloverObject?.messageElement?.mounted && cloverFooter) {
      cloverFooter.classList.add('is-hidden');
      setIsCloverLoading(false);
    }
    if (!cloverObject?.messageElement?.mounted) {
      setIsCloverLoading(true);
    }
  }, [cloverObject?.messageElement?.mounted]);

  useEffect(() => {
    if (cloverFormErrors) {
      Object.keys(cloverFormErrors).forEach((key) => {
        const typedKey = key as CloverFormErrorsKeys;
        const value = cloverFormErrors[typedKey] as string;
        if (key === CARD_NUMBER_TYPE) {
          formFieldErrorHandler(
            value,
            setCardNumberError,
            formatMessage(messages.invalidCard),
            formatMessage(messages.invalidCard),
          );
        }
        if (key === CARD_EXPIRY_TYPE) {
          formFieldErrorHandler(
            value,
            setCardExpiryDateError,
            formatMessage(messages.invalidExp),
            formatMessage(messages.invalidExp),
          );
        }
        if (key === CARD_SECURITY_CODE_TYPE) {
          formFieldErrorHandler(
            value,
            setCardSecurityCodeError,
            formatMessage(messages.emptySecurityCode),
            formatMessage(messages.invalidSecurityCode),
          );
        }
        if (key === CARD_POSTAL_CODE_TYPE) {
          formFieldErrorHandler(
            value,
            setCardPostalCodeError,
            formatMessage(messages.emptyPostalCode),
            formatMessage(messages.invalidPostalCode),
          );
        }
        return key;
      });
    }
  }, [cloverFormErrors, formFieldErrorHandler, formatMessage]);

  return (
    <form
      className={styles['clover-form']}
      action="/charge"
      method="post"
      id="payment-form"
    >
      <div id="display-error" />
      <CloverInput
        id={formatMessage(messages.cardNumberInputId)}
        error={cardNumberError}
        isLoading={isCloverLoading}
      />
      <div className={styles['clover-small-fields-container']}>
        <div className={styles['expiry-date-security-code']}>
          <CloverInput
            id={formatMessage(messages.cardExpiryDateInputId)}
            error={cardExpiryDateError}
            isLoading={isCloverLoading}
          />
          <CloverInput
            id={formatMessage(messages.cardSecurityCodeInputId)}
            error={cardSecurityCodeError}
            isLoading={isCloverLoading}
          />
        </div>
      </div>
      <div>
        <div className={styles['zip-code']}>
          <CloverInput
            id={formatMessage(messages.cardPostalCodeInputId)}
            error={cardPostalCodeError}
            isLoading={isCloverLoading}
          />
        </div>
      </div>
      <div
        id="card-response"
        role="alert"
        className={styles['card-response']}
      />
    </form>
  );
};

CloverForm.propTypes = {};

CloverForm.defaultProps = {};

export default CloverForm;
